import React from "react";
import { Link } from "react-router-dom";

import talentLenzWave from "../../images/talentlenz-luminous-wave.svg";
import talentLenzMobileDashboard from "../../images/talentlenz-match-dashboard-desktop.svg";
import "./TalentLenzSection.css";

const rankings = [
  { initials: "AB", score: 92, primary: 88, secondary: 55 },
  { initials: "CD", score: 78, primary: 86, secondary: 40 },
  { initials: "EF", score: 71, primary: 82, secondary: 38 },
  { initials: "GH", score: 64, primary: 76, secondary: 36 },
  { initials: "IJ", score: 56, primary: 69, secondary: 34 },
];

function ArrowRight({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m8 10 4 4 4-4" />
    </svg>
  );
}

function TechnicalIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m8.5 5-6 7 6 7" />
      <path d="m15.5 5 6 7-6 7" />
      <path d="m13.5 3-3 18" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="7.5" r="3.3" />
      <path d="M5.5 20c0-4.1 2.8-6.6 6.5-6.6s6.5 2.5 6.5 6.6" />
      <path d="M8.7 19.5h6.6" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2.8 20 6v5.8c0 4.7-3.1 8.1-8 9.7-4.9-1.6-8-5-8-9.7V6l8-3.2Z" />
      <path d="m8.5 12 2.2 2.2 4.8-5" />
    </svg>
  );
}

function ScalesIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v17" />
      <path d="M7 21h10" />
      <path d="M5 6h14" />
      <path d="m5 6-3 7h6L5 6Z" />
      <path d="m19 6-3 7h6l-3-7Z" />
      <path d="M2 13c.5 1.7 1.5 2.5 3 2.5S7.5 14.7 8 13" />
      <path d="M16 13c.5 1.7 1.5 2.5 3 2.5s2.5-.8 3-2.5" />
    </svg>
  );
}

function RankingRow({ candidate, index }) {
  const isTopMatch = index === 0;

  return (
    <li className={`tl-ranking-row ${isTopMatch ? "is-top-match" : ""}`}>
      <span className="tl-ranking-row__position">{index + 1}</span>

      <span className="tl-ranking-row__avatar">
        {candidate.initials}
      </span>

      <span className="tl-ranking-row__bars" aria-hidden="true">
        <i
          className="tl-ranking-row__bar tl-ranking-row__bar--primary"
          style={{ "--tl-ranking-width": `${candidate.primary}%` }}
        />
        <i
          className="tl-ranking-row__bar tl-ranking-row__bar--secondary"
          style={{ "--tl-ranking-width": `${candidate.secondary}%` }}
        />
      </span>

      <strong>{candidate.score}%</strong>
    </li>
  );
}

function Metric({
  label,
  score,
  tone = "blue",
  Icon,
}) {
  return (
    <div className={`tl-metric tl-metric--${tone}`}>
      <span className="tl-metric__icon">
        <Icon />
      </span>

      <span className="tl-metric__label">{label}</span>

      <span className="tl-metric__progress" aria-hidden="true">
        <i style={{ "--tl-metric-progress": `${score}%` }} />
      </span>

      <strong>{score}%</strong>
    </div>
  );
}

function DesktopDashboard() {
  return (
    <section
      className="tl-dashboard tl-dashboard--desktop"
      aria-label="TalentLenz opportunity match example"
    >
      <header className="tl-dashboard__header">
        <h3>Opportunity match</h3>
        <p>Ranked. Explained. Interview-ready.</p>
      </header>

      <div className="tl-dashboard__content">
        <div className="tl-ranking-panel">
          <ol className="tl-ranking-list">
            {rankings.map((candidate, index) => (
              <RankingRow
                key={candidate.initials}
                candidate={candidate}
                index={index}
              />
            ))}
          </ol>

          <button
            type="button"
            className="tl-ranking-panel__button"
            aria-label="View full candidate rankings"
          >
            <span>View full rankings</span>
            <ChevronDown />
          </button>
        </div>

        <div className="tl-match-panel">
          <div className="tl-score-radar">
            <span className="tl-score-radar__axis tl-score-radar__axis--horizontal" />
            <span className="tl-score-radar__axis tl-score-radar__axis--vertical" />

            <span className="tl-score-radar__point tl-score-radar__point--one" />
            <span className="tl-score-radar__point tl-score-radar__point--two" />
            <span className="tl-score-radar__point tl-score-radar__point--three" />

            <span className="tl-score-radar__ring tl-score-radar__ring--one" />
            <span className="tl-score-radar__ring tl-score-radar__ring--two" />
            <span className="tl-score-radar__ring tl-score-radar__ring--three" />
            <span className="tl-score-radar__ring tl-score-radar__ring--four" />

            <span className="tl-score-radar__badge">Top match</span>

            <div className="tl-score-orb">
              <span className="tl-score-orb__avatar">AB</span>
              <span className="tl-score-orb__label">Suitability score</span>
              <strong>92%</strong>
            </div>
          </div>

          <div className="tl-metrics">
            <Metric
              label="Technical competency"
              score={94}
              Icon={TechnicalIcon}
            />

            <Metric
              label="Workplace awareness"
              score={89}
              tone="pink"
              Icon={PersonIcon}
            />

            <Metric
              label="Profile validity"
              score={93}
              Icon={ShieldIcon}
            />
          </div>
        </div>
      </div>

      <footer className="tl-dashboard__footer">
        <span className="tl-dashboard__footer-icon">
          <ShieldIcon />
        </span>

        <p>
          Evidence-based ranking. Human decision.
          <br />
          HR teams retain responsibility for decisions.
        </p>

        <span className="tl-dashboard__footer-scales">
          <ScalesIcon />
        </span>
      </footer>
    </section>
  );
}

export default function TalentLenzSection() {
  return (
    <section
      id="talentlenz"
      className="tl-section"
      aria-labelledby="talentlenz-title"
    >
      <img
        className="tl-section__wave"
        src={talentLenzWave}
        alt=""
        aria-hidden="true"
      />

      <div className="tl-section__inner">
        <div className="tl-section__copy">
          <p className="tl-section__eyebrow">TalentLenz</p>

          <h2 id="talentlenz-title">
            Find the right person for every opportunity.
          </h2>

          <p className="tl-section__body">
            Custom AI assesses every application against the opportunity,
            ranks suitability and gives HR the evidence to shortlist
            fairly—faster.
          </p>

          <Link
            className="tl-section__button"
            to="/talentlenz"
            aria-label="Explore TalentLenz"
          >
            <span>Explore TalentLenz</span>
            <ArrowRight />
          </Link>
        </div>

        <div className="tl-section__visual">
          <DesktopDashboard />

          <div className="tl-dashboard-mobile">
            <img
              className="tl-dashboard-mobile__image"
              src={talentLenzMobileDashboard}
              alt="TalentLenz opportunity match dashboard showing candidate AB as the top match with a 92 percent suitability score"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}